import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  message,
  Modal,
  Row,
  Select,
  Table,
  Upload,
} from "antd";
import axios from "axios";
import moment from "moment";
import { useCallback, useState } from "react";
import { MdFileDownload, MdUpload } from "react-icons/md";
import * as XLSX from "xlsx";
import { API } from "../../../Config";
import File from "../../Delivery/excel/PURCHASE_EXCELL_FILE.xlsx";
import { useSelector } from "react-redux";

const PurchaseForm = (props) => {
  const Auth = useSelector((state) => state.Auth.user);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const dateFormat = "DD/MM/YYYY";
  const selectedPurchase = props?.selectedPurchase || "";

  const getInitialDataSource = () => {
    if (props?.selectedPurchase?.purchaseDetails?.length > 0) {
      return props?.selectedPurchase?.purchaseDetails?.map((item) => ({
        ...item,
        key: item.id || Date.now() + Math.random(),
        discount: item.discount || 0,
      }));
    } else {
      return [
        {
          key: Date.now(),
          name: "",
          price: 1,
          description: "",
          discount: 0,
          quantity: 1,
        },
      ];
    }
  };

  const [dataSource, setDataSource] = useState(getInitialDataSource());

  const handleFinish = async (values) => {
    try {
      setLoading(true);
      const invalidRow = dataSource.find(
        (item) => !item.quantity || Number(item.quantity) <= 0
      );
      if (invalidRow) {
        message.error("Please enter valid quantity for all products.");
        setLoading(false);
        return;
      }

      const payload = {
        description: values.description,
        supplierId: values.supplierId,
        userid: Number(Auth?.id),
        payment_date: values.purchase_date,
        items: dataSource.map((item) => ({
          name: item?.name?.toString(),
          price: Number(item.price),
          discount: Number(item.discount) || 0,
          quantity: Number(item.quantity),
          description: item.description || "",
          key: Number(item.id),
        })),
      };

      const response = selectedPurchase?.id
        ? await axios.put(
            `${API.BASR_URL}${API.PURCHASE}/${selectedPurchase.id}`,
            payload
          )
        : await axios.post(`${API.BASR_URL}${API.PURCHASE}`, payload);
      if (response?.status) {
        message.success(
          `Purchase ${selectedPurchase?.id ? "updated" : "added"}  successfully`
        );
        resetFormAndData();
        props?.onClose();
        props?.relaod();
      }
      setLoading(false);
    } catch (error) {
      message.error(`Oops. Something went wrong ${error}`);
      console.error("Update amount field error:", error);
      setLoading(false);
    }
  };

  const renderSummary = () => {
    const totalQuantity = dataSource?.reduce(
      (acc, item) => acc + Number(item.quantity || 0),
      0
    );
    const totalPrice = dataSource?.reduce(
      (acc, item) =>
        acc +
        (Number(item.price) * Number(item.quantity) -
          Number(item.discount || 0)),
      0
    );
    return (
      <Table.Summary.Row style={{ height: 50 }}>
        <Table.Summary.Cell index={0} />
        <Table.Summary.Cell index={1}>
          <Button onClick={handleAddProduct}>Add New</Button>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={2} />
        <Table.Summary.Cell index={3}>
          <strong>Total Quantity</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={4}>
          <strong>{totalQuantity || 0}</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={5}>
          <strong>Total Price</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={5}>
          <strong>{Number(totalPrice || 0).toFixed(2)}</strong>
        </Table.Summary.Cell>
      </Table.Summary.Row>
    );
  };
  const handleChangeValueSet = useCallback((value, key, field) => {
    setDataSource((prev) => {
      const newDataSource = prev.map((val) => {
        if (val.key === key) {
          const updates = { [field]: value };

          // Validation: discount should not exceed price
          if (field === "discount") {
            const numericPrice = Number(val.price || 0);
            const numericDiscount = Number(value || 0);
            if (numericDiscount > numericPrice) {
              Modal.warning({
                title: "Invalid Discount",
                content: "Discount cannot be greater than the price.",
              });
              return val; // don't update
            }
          }

          return { ...val, ...updates };
        }
        return val;
      });

      return newDataSource;
    });
  }, []);

  const handleAddProduct = useCallback(() => {
    setDataSource((prev) => [
      ...prev,
      {
        key: Date.now(), // Unique key
        name: "",
        price: 0,
        discount: 0,
        quantity: 1,
      },
    ]);
  }, []);

  const handleExcelUpload = (file) => {
    setUploading(true);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        // Transform Excel data to match table structure
        const transformedData = jsonData.map((row, index) => ({
          key: Date.now() + index,
          name: row.Name || row.name || "",
          description: row.Description || row.description || "",
          price: Number(row.Price || row.price || 0),
          discount: Number(row.Discount || row.discount || 0), // Default to 0 if not provided
          quantity: Number(row.Quantity || row.quantity || 1),
        }));

        // Validate the data
        const isValidData = transformedData.every(
          (item) =>
            item.name &&
            item.description &&
            !isNaN(item.price) &&
            !isNaN(item.discount) &&
            !isNaN(item.quantity) &&
            item.price >= 0 &&
            item.discount >= 0 &&
            item.quantity > 0
        );

        if (isValidData) {
          setDataSource(transformedData);
          message.success(
            `Successfully imported ${transformedData.length} products from Excel`
          );
        } else {
          message.error(
            "Invalid data format in Excel file. Please check the sample format."
          );
        }
      } catch (error) {
        message.error(
          "Error reading Excel file. Please ensure it's a valid Excel file."
        );
        console.error("Excel upload error:", error);
      } finally {
        setUploading(false);
      }
    };

    reader.readAsArrayBuffer(file);
    return false; // Prevent default upload behavior
  };

  const handleRemoveProduct = useCallback((key) => {
    setDataSource((prev) => prev.filter((item) => item.key !== key));
  }, []);

  const columns = [
    {
      title: "Sl",
      dataIndex: "slno",
      key: "slno",
      width: 30,
      align: "center",
      fixed: "left",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <Input.TextArea
          autoSize={{ minRows: 1, maxRows: 6 }}
          bordered={false}
          value={record.name}
          onChange={(e) =>
            handleChangeValueSet(e.target.value, record.key, "name")
          }
        />
      ),
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      render: (_, record) => (
        <Input
          type="number"
          min={0}
          bordered={false}
          value={record.price}
          onKeyDown={(e) => {
            if (e.key === "e" || e.key === "E") {
              e.preventDefault();
            }
          }}
          onChange={(e) =>
            handleChangeValueSet(e.target.value, record.key, "price")
          }
        />
      ),
    },
    {
      title: "Discount",
      dataIndex: "discount",
      key: "discount",
      render: (_, record) => (
        <Input
          type="number"
          min={0}
          bordered={false}
          value={record.discount}
          onKeyDown={(e) => {
            if (e.key === "e" || e.key === "E") {
              e.preventDefault();
            }
          }}
          onChange={(e) =>
            handleChangeValueSet(e.target.value, record.key, "discount")
          }
        />
      ),
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      render: (_, record) => (
        <Input
          type="number"
          min={0}
          bordered={false}
          value={record.quantity}
          onKeyDown={(e) => {
            if (e.key === "e" || e.key === "E") {
              e.preventDefault();
            }
          }}
          onChange={(e) =>
            handleChangeValueSet(e.target.value, record.key, "quantity")
          }
        />
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (_, record) => (
        <Input
          bordered={false}
          value={record.description}
          onKeyDown={(e) => {
            if (e.key === "e" || e.key === "E") {
              e.preventDefault();
            }
          }}
          onChange={(e) =>
            handleChangeValueSet(e.target.value, record.key, "description")
          }
        />
      ),
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      render: (_, record) => {
        const itemTotal =
          Number(record.price) * Number(record.quantity) -
          Number(record.discount || 0);
        return <span>{Number(itemTotal).toFixed(2)}</span>;
      },
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (_, record) => (
        <Button
          danger
          type="link"
          onClick={() => handleRemoveProduct(record.key)}
        >
          Remove
        </Button>
      ),
    },
  ];
  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = File;
    link.download = "PURCHASE_EXCELL_FILE.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const resetFormAndData = () => {
    form.resetFields();
    setDataSource([]);
  };
  return (
    <Modal
      title={selectedPurchase?.id ? "Update Purchase" : "Add Purchase"}
      open={props.visible}
      onCancel={props.onClose}
      footer={false}
      width={900}
    >
      <Form
        form={form}
        onFinish={handleFinish}
        initialValues={{
          supplierId: selectedPurchase?.supplierDetails?.account_no,
          purchase_date: selectedPurchase?.payment_date
            ? moment(selectedPurchase.payment_date)
            : null,
          description: selectedPurchase?.description,
        }}
      >
        <Row>
          <Col md={12}>
            <Form.Item
              label="Supplier"
              name="supplierId"
              rules={[{ required: true }]}
              labelCol={{ span: 7 }}
            >
              <Select
                size="medium"
                showSearch
                allowClear
                filterOption={(input, option) => {
                  const searchText = input.toLowerCase();
                  const optionText = option.children.toString().toLowerCase();
                  return optionText.includes(searchText);
                }}
              >
                {props.accounts?.map((item) => (
                  <Select.Option key={item.account_no} value={item.account_no}>
                    {item.account_no} - {item.account_name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label="Purchase Date"
              name="purchase_date"
              rules={[{ required: true }]}
              labelCol={{ span: 7 }}
            >
              <DatePicker
                size="medium"
                format={dateFormat}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col md={12}>
            <Form.Item
              label="Description"
              name="description"
              labelCol={{ span: 7 }}
            >
              <Input.TextArea rows={1} size="medium" />
            </Form.Item>
            <Form.Item
              label="Upload Excel"
              labelCol={{ span: 7 }}
              className="g-3"
            >
              <Upload
                beforeUpload={handleExcelUpload}
                accept=".xlsx,.xls"
                showUploadList={false}
                loading={uploading}
              >
                <Button loading={uploading} icon={<MdUpload />}>
                  {uploading ? "Uploading..." : "Upload Excel"}
                </Button>
              </Upload>
              &nbsp;&nbsp;&nbsp;
              <Button onClick={handleDownload} icon={<MdFileDownload />}>
                Sample File
              </Button>
            </Form.Item>
          </Col>
          <Col md={8} />
        </Row>
      </Form>
      <Table
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        summary={renderSummary}
      />
      <Row justify="end" gutter={[16, 0]} style={{ marginTop: 16 }}>
        <Col>
          <Button
            onClick={() => {
              resetFormAndData();
              props.onClose();
            }}
          >
            Cancel
          </Button>
        </Col>
        <Col>
          <Button
            type="primary"
            onClick={() => form.submit()}
            loading={loading}
          >
            {selectedPurchase?.id ? "Update" : "Save"}
          </Button>
        </Col>
      </Row>
    </Modal>
  );
};

export default PurchaseForm;
